import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  LinearProgress,
  Avatar,
  Tooltip
} from '@mui/material';
import { UploadFile as UploadFileIcon, DocumentScanner as DocumentScannerIcon, ShoppingCart as ProductIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { getImageUrl } from '../../config/api.config';
import useStopwatch, { formatElapsed } from '../../hooks/useStopwatch';
import NeuroMetricsChips from './NeuroMetricsChips';
import ScanResultDetails from '../ScanResultDetails';

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
};

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return '';
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return getImageUrl(photoUrl);
};

/**
 * Вкладка «Сканирование»: тест распознавания растения по фото
 * (аналог сканирования в мобильном приложении — только фото, без текста).
 */
const NeuroScanTester = ({ onRequestDone }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const { elapsedMs, start, stop } = useStopwatch();

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
    setResult(null);
    setError(null);
  };

  const handleScan = async () => {
    if (!file || loading) return;

    setLoading(true);
    setError(null);
    setResult(null);
    start();

    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await apiService.neuro.testScan(formData);
      setResult(response.data?.data || null);
      if (onRequestDone) onRequestDone();
    } catch (err) {
      console.error('Ошибка тестового сканирования:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      stop();
      setLoading(false);
    }
  };

  const cost = result?.cost;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>Тест сканирования</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Загрузите фото растения — как в мобильном приложении
        </Typography>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
          >
            Выбрать фото
          </Button>
          <Button
            variant="contained"
            startIcon={<DocumentScannerIcon />}
            onClick={handleScan}
            disabled={!file || loading}
          >
            Сканировать
          </Button>
          {file && (
            <Typography variant="caption" color="text.secondary">
              {file.name} ({formatFileSize(file.size)})
            </Typography>
          )}
        </Box>

        {previewUrl && (
          <Box
            component="img"
            src={previewUrl}
            alt="Превью выбранного фото"
            sx={{ maxHeight: 240, maxWidth: '100%', borderRadius: 1, display: 'block', mb: 2 }}
          />
        )}

        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress sx={{ mb: 1 }} />
            <Typography
              variant="h6"
              sx={{ fontFamily: 'monospace', textAlign: 'center' }}
            >
              Прошло: {formatElapsed(elapsedMs)}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {result && (
          <Box>
            <Box sx={{ mb: 1 }}>
              <NeuroMetricsChips
                timeMs={result.processing_time ?? elapsedMs}
                inputTokens={cost?.inputTokens}
                outputTokens={cost?.outputTokens}
                totalTokens={
                  cost?.inputTokens != null && cost?.outputTokens != null
                    ? cost.inputTokens + cost.outputTokens
                    : null
                }
                costUsd={cost?.usd}
                model={result.model || cost?.model}
              />
            </Box>

            {result.scan_id && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={`Тестовое сканирование сохранено в истории (scan_id: ${result.scan_id})`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Box>
            )}

            {result.photo_url && (
              <Box
                component="img"
                src={resolvePhotoUrl(result.photo_url)}
                alt="Фото результата сканирования"
                sx={{ maxHeight: 240, maxWidth: '100%', borderRadius: 1, display: 'block', mb: 2 }}
              />
            )}

            <ScanResultDetails
              plantInfo={result.plant_info}
              recommendedProducts={result.recommended_products}
            />

            {Array.isArray(result.recommended_products) && result.recommended_products.length > 0 && (
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Рекомендованные товары
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {result.recommended_products.map((product, index) => {
                      const tooltipParts = [product.disease_name, product.product_type, product.dosage].filter(Boolean);
                      const chip = (
                        <Chip
                          icon={product.product_image ? undefined : <ProductIcon />}
                          avatar={product.product_image ? <Avatar src={product.product_image} /> : undefined}
                          label={product.product_name || 'Товар'}
                          size="small"
                          color="primary"
                          variant="outlined"
                          clickable={!!product.purchase_link}
                          component={product.purchase_link ? 'a' : 'div'}
                          href={product.purchase_link || undefined}
                          target={product.purchase_link ? '_blank' : undefined}
                          rel={product.purchase_link ? 'noopener noreferrer' : undefined}
                        />
                      );
                      return tooltipParts.length > 0 ? (
                        <Tooltip key={product._id || index} title={tooltipParts.join(' • ')}>
                          {chip}
                        </Tooltip>
                      ) : (
                        <React.Fragment key={product._id || index}>{chip}</React.Fragment>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default NeuroScanTester;
